import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import type { BeratungFormField } from "@/hooks/useBeratungSeiten";
import {
  ImageSelectField,
  TimelineSlider,
  StarRatingField,
  DualColumnTable,
  CalculatorField,
  RadioGroupField,
  NotesField,
  MultiPersonTable,
  ChildrenDataTable,
  IncomeExpenseTable,
  InsuranceChecklistTable,
} from "./fields";

interface BeraterFormFieldProps {
  field: BeratungFormField;
  value: any;
  onChange: (value: any) => void;
  formValues?: Record<string, any>;
}

export function BeraterFormField({ field, value, onChange, formValues = {} }: BeraterFormFieldProps) {
  // Check conditional visibility
  if (field.conditional) {
    const conditionMet = formValues[field.conditional.field] === field.conditional.value;
    if (!conditionMet) return null;
  }

  const renderField = () => {
    switch (field.type) {
      case 'select':
        return (
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger>
              <SelectValue placeholder={`${field.label} auswählen`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.id}
              checked={!!value}
              onCheckedChange={onChange}
            />
            <label
              htmlFor={field.id}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {field.label}
            </label>
          </div>
        );
      
      case 'currency':
        return (
          <div className="relative">
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
              placeholder={field.placeholder}
              className="pr-8"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              €
            </span>
          </div>
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={value ?? field.default ?? ''}
            onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}
            placeholder={field.placeholder}
          />
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      
      case 'email':
        return (
          <Input
            type="email"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || 'email@example.com'}
          />
        );
      
      case 'tel':
        return (
          <Input
            type="tel"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || '+43...'}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            rows={4}
          />
        );

      case 'image-select':
        return (
          <ImageSelectField
            options={field.imageOptions || []}
            value={value}
            onChange={onChange}
            multiple={field.multiple}
            columns={field.columns}
          />
        );

      case 'timeline-slider':
        return (
          <TimelineSlider
            options={field.options || []}
            value={value}
            onChange={onChange}
          />
        );

      case 'star-rating':
        return (
          <StarRatingField
            value={value || 0}
            onChange={onChange}
            maxStars={field.maxStars}
            labels={field.starLabels}
          />
        );

      case 'dual-column-table':
        return (
          <DualColumnTable
            rows={field.tableRows || []}
            column1Label={field.column1Label || 'GP1'}
            column2Label={field.column2Label || 'GP2'}
            value={value || { column1: {}, column2: {} }}
            onChange={onChange}
          />
        );

      case 'calculator':
        return (
          <CalculatorField
            rows={field.calculatorRows || []}
            value={value || {}}
            onChange={onChange}
          />
        );

      case 'radio-group':
        return (
          <RadioGroupField
            options={field.options || []}
            value={value}
            onChange={onChange}
            orientation={field.orientation}
          />
        );

      case 'notes':
        return (
          <NotesField
            value={value || ''}
            onChange={onChange}
            placeholder={field.placeholder}
          />
        );

      case 'multi-person-table':
        return (
          <MultiPersonTable
            label={field.label}
            value={value || { gp1: [], gp2: [] }}
            onChange={onChange}
          />
        );

      case 'children-data-table':
        return (
          <ChildrenDataTable
            value={value || []}
            onChange={onChange}
          />
        );

      case 'income-expense-table':
        return (
          <IncomeExpenseTable
            value={value || {}}
            onChange={onChange}
          />
        );

      case 'insurance-checklist-table':
        return (
          <InsuranceChecklistTable
            items={[
              { id: 'haushalt', label: 'Haushaltsversicherung', category: 'Sachversicherung' },
              { id: 'rechtsschutz', label: 'Rechtsschutz', category: 'Sachversicherung' },
              { id: 'kfz', label: 'KFZ-Versicherung', category: 'Sachversicherung' },
              { id: 'leasing', label: 'Leasing/Kasko', category: 'Sachversicherung' },
              { id: 'unfall', label: 'Unfallversicherung', category: 'Personenversicherung' },
              { id: 'bu', label: 'Berufsunfähigkeit', category: 'Personenversicherung' },
              { id: 'taggeld', label: 'Krankentaggeld', category: 'Personenversicherung' },
              { id: 'ableben', label: 'Ablebensversicherung', category: 'Personenversicherung' },
            ]}
            value={value || {}}
            onChange={onChange}
          />
        );
      
      default:
        return (
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
          />
        );
    }
  };

  if (field.type === 'checkbox') {
    return <div className="py-2">{renderField()}</div>;
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={field.id}>
        {field.label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {renderField()}
    </div>
  );
}
